import React from "react";
import { useParams, Link } from "react-router-dom";
import { getProduct, PRODUCTS } from "@/lib/products";
import { LOGO_URL } from "@/lib/brand";
import AnimatedBackground from "@/components/AnimatedBackground";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Sparkles, Rotate3d } from "lucide-react";

const GOLD = "#B2A475";
const GOLD_RGBA = (a) => `rgba(178,164,117,${a})`;

const ProductDetail = () => {
  const { id } = useParams();
  const product = getProduct(id);

  if (!product) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] text-white flex flex-col items-center justify-center gap-4">
        <p>Prodotto non trovato.</p>
        <Link to="/" style={{ color: GOLD }}>Torna alla home</Link>
      </div>
    );
  }
  const others = PRODUCTS.filter((p) => p.id !== id);

  return (
    <div className="min-h-screen relative" style={{ color: "#F5F1E6", fontFamily: "'Manrope', sans-serif", backgroundColor: "#0B0B0C" }}>
      <AnimatedBackground base="#0B0B0C" accent={GOLD} />
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl border-b" style={{ backgroundColor: "rgba(11,11,12,0.72)", borderColor: GOLD_RGBA(0.18) }}>
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="back-home">
            <img src={LOGO_URL} alt="logo" className="w-9 h-9 object-contain" />
            <span className="tracking-widest uppercase" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.1rem" }}>The Cube Design</span>
          </Link>
          <Link to="/#prodotti" className="text-sm flex items-center gap-1.5" style={{ color: "rgba(245,241,230,0.6)" }}><ArrowLeft className="w-4 h-4" /> Prodotti</Link>
        </div>
      </header>

      <section className="relative z-10 max-w-6xl mx-auto px-5 pt-28 pb-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} className="relative rounded-3xl overflow-hidden" style={{ border: `1px solid ${GOLD_RGBA(0.25)}` }}>
            <img src={product.image} alt={product.name} className="w-full h-[380px] md:h-[520px] object-cover" data-testid="product-detail-image" />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}>
            <span className="text-xs uppercase tracking-[0.35em]" style={{ color: GOLD }}>{product.tagline}</span>
            <h1 className="mt-4 font-medium text-4xl md:text-6xl gold-text" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{product.name}</h1>
            <p className="mt-6 text-lg leading-relaxed" style={{ color: "rgba(245,241,230,0.6)" }}>{product.description}</p>
            <ul className="mt-8 space-y-3">
              {product.features.map((f) => (
                <li key={f} className="flex items-center gap-3" style={{ color: "rgba(245,241,230,0.8)" }}>
                  <span className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: GOLD_RGBA(0.2) }}><Check className="w-3 h-3" style={{ color: GOLD }} /></span>{f}
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/register" data-testid="product-trial" className="rounded-full font-semibold px-8 py-3.5 transition-shadow hover:shadow-[0_0_40px_-6px_#B2A475] flex items-center gap-2" style={{ backgroundColor: GOLD, color: "#0B0B0C" }}><Sparkles className="w-4 h-4" /> Prova gratis</Link>
              <Link to="/configura" data-testid="product-configure" className="rounded-full font-semibold px-8 py-3.5 border transition-colors hover:bg-white/5 flex items-center gap-2" style={{ borderColor: GOLD_RGBA(0.4), color: GOLD }}><Rotate3d className="w-4 h-4" /> Personalizza il tuo cubo</Link>
              <Link to="/#contatti" data-testid="product-cta" className="rounded-full font-semibold px-8 py-3.5 border transition-colors hover:bg-white/5" style={{ borderColor: GOLD_RGBA(0.4), color: "#F5F1E6" }}>Richiedi informazioni</Link>
            </div>
          </motion.div>
        </div>

        <div className="mt-24">
          <h2 className="font-medium text-3xl mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Altri prodotti</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {others.map((p) => (
              <Link key={p.id} to={`/prodotti/${p.id}`} className="group rounded-2xl overflow-hidden" style={{ border: `1px solid ${GOLD_RGBA(0.2)}`, backgroundColor: "#121212" }} data-testid={`related-${p.id}`}>
                <div className="h-40 overflow-hidden"><img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-4"><h3 className="font-medium text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{p.name}</h3></div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
